// src/App.tsx - FIXED: Prevents canvas growing issue
/**
 * T-37 INTEGRATION: Updated to use NumberLineCanvas instead of direct NumberLine
 * 
 * FIXES APPLIED:
 * - Fixed dimension calculation to prevent infinite growth
 * - Stabilized container measurements
 * - Added proper dimension clamping
 * - Removed circular dependency in resize observer
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './types/redux';

// T-34: Import AppShell component (unchanged)
import { AppShell } from './components/AppShell';

// T-37: Import new integrated canvas component
import { NumberLineCanvas } from './components/NumberLineCanvas';

// Import ModalProvider (unchanged)
import { ModalProvider } from './components/ui';

// Import existing components (unchanged)
import { AutoLoadPrompt } from './components/AutoLoadPrompt';
import { InstallPrompt } from './components/InstallPrompt';
import { OfflineStatus } from './components/OfflineStatus';

// Import existing action creators (unchanged)
import { 
  setDetectedSceneId,
  setAutoLoadingState,
  setAutoLoadError,
  dismissAutoLoadPrompt,
  loadMeasurementsState,
} from './store/slices/uiSlice';
import { 
  setLoadedFromShare
} from './store/slices/shareSlice';
import { centerOrigin, loadViewportState, setOrientation } from './store/slices/viewportSlice';
import { loadNumberLineState } from './store/slices/numberLinesSlice';
import { loadSecondaryLineState } from './store/slices/secondaryLineSlice';
import { loadEquivalentFractionsState } from './store/slices/equivalentFractionsSlice';
import { loadExpressionsState } from './store/slices/expressionsSlice';
import { loadJumpIndicatorsState } from './store/slices/jumpIndicatorsSlice';
import { loadAnnotationsState } from './store/slices/annotationsSlice';
import { loadConstantsState } from './store/slices/constantsSlice';
import { updatePreferences } from './store/slices/preferencesSlice';
import { loadLabelVisibilityState } from './store/slices/labelVisibilitySlice';
import { loadCustomTicksState } from './store/slices/customTicksSlice';
import { setMultiplierMarkers } from './store/slices/multiplierSlice';
import { loadDNLToolsState } from './store/slices/dnlToolsSlice';
import { loadSharedScene } from './lib/shareApi';

// T-37: Import layer management CSS
import './styles/layers.css';

function App() {
  const dispatch = useDispatch();
  
  // Safe selector usage to prevent "unknown selector" errors (unchanged)
  const preferences = useSelector((state: RootState) => state.preferences || {});
  const shareUrlState = useSelector((state: RootState) => state.ui?.shareUrlState || {});
  const uiState = useSelector((state: RootState) => state.ui || {});
  const viewport = useSelector((state: RootState) => state.viewport);
  const isVertical = viewport?.orientation === 'vertical';
  const viewMode = useSelector((state: RootState) => state.equivalentFractions?.mode ?? 'standard');
  const isEquivalentMode = viewMode === 'equivalentFractions';
  
  // FIXED: Stable dimensions with better clamping
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  // Keep a stable render size (max seen) to lock visual scale even when viewport shrinks
  const maxDimensionsRef = useRef<{ width: number; height: number }>({ width: 800, height: 400 });
  // Smooth resize target and RAF for interpolation
  const targetRef = useRef<{ width: number; height: number }>({ width: 800, height: 400 });
  const resizeRafRef = useRef<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Track fullscreen state for expanded canvas
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Ref for measuring the container (unchanged)
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  // Ref for the NumberLineCanvas component (for export functionality)
  const numberLineRef = useRef<HTMLDivElement>(null);

  // FIXED: Improved dimension calculation with stability checks
  // Updated to account for vertical orientation
  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const inFullscreen = !!document.fullscreenElement;
      
      // Calculate new dimensions with proper constraints
      // In fullscreen: use full available space with minimal padding
      // Normal mode: use constrained dimensions
      // For vertical mode, prioritize height over width
      let newWidth: number;
      let newHeight: number;
      
      if (inFullscreen) {
        newWidth = Math.max(400, rect.width - 16);
        newHeight = Math.max(400, Math.floor(window.innerHeight * 0.9));
      } else if (isVertical) {
        // Vertical mode: give more width so multiple equivalent lines fit
        newWidth = Math.max(700, Math.min(1400, rect.width - 16));
        newHeight = Math.max(520, Math.min(1100, Math.floor(window.innerHeight * 0.82)));
      } else {
        // Horizontal mode: wider width, shorter height (slightly taller for equivalent fractions)
        newWidth = Math.max(400, Math.min(1600, rect.width - 32)); // Max width limit in normal mode
        const baseHeight = Math.max(420, Math.min(950, Math.floor(window.innerHeight * 0.72)));
        newHeight = isEquivalentMode ? Math.min(1100, Math.floor(baseHeight * 1.2)) : baseHeight;
      }
      
      // Update target for smooth interpolation
      targetRef.current = { width: newWidth, height: newHeight };

      // Track the maximum size we've seen (toolbar closed state)
      if (newWidth > maxDimensionsRef.current.width) maxDimensionsRef.current.width = newWidth;
      if (newHeight > maxDimensionsRef.current.height) maxDimensionsRef.current.height = newHeight;
      
      // Start RAF interpolation if not running
      if (resizeRafRef.current === null) {
        const step = () => {
          setDimensions(prev => {
            const tw = targetRef.current.width;
            const th = targetRef.current.height;
            const dw = tw - prev.width;
            const dh = th - prev.height;
            // Stop when close enough
            if (Math.abs(dw) < 0.5 && Math.abs(dh) < 0.5) {
              resizeRafRef.current && cancelAnimationFrame(resizeRafRef.current);
              resizeRafRef.current = null;
              return { width: tw, height: th };
            }
            // Lerp towards target for smooth animation
            const factor = 0.25; // easing factor
            const next = { width: prev.width + dw * factor, height: prev.height + dh * factor };
            return next;
          });
          if (resizeRafRef.current !== null) {
            resizeRafRef.current = requestAnimationFrame(step);
          }
        };
        resizeRafRef.current = requestAnimationFrame(step);
      }
      
      // Mark as initialized after first measurement
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [isInitialized, isVertical]);
  
  // Track fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const inFullscreen = !!document.fullscreenElement;
      setIsFullscreen(inFullscreen);
      // Reset max dimensions when entering/exiting fullscreen to allow proper resizing
      if (inFullscreen) {
        // In fullscreen, allow larger dimensions
        maxDimensionsRef.current = { width: window.innerWidth, height: window.innerHeight };
      } else {
        // Reset to reasonable defaults when exiting fullscreen
        maxDimensionsRef.current = { width: 800, height: 400 };
      }
      // Immediately update dimensions
      updateDimensions();
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [updateDimensions]);

  // FIXED: Improved resize handling with debouncing
  useEffect(() => {
    // Initial measurement
    updateDimensions();
    
    // Throttled handler to feed target updates smoothly
    let resizeTimeout: number | undefined;
    const throttledUpdateDimensions = () => {
      if (resizeTimeout !== undefined) return;
      resizeTimeout = window.setTimeout(() => {
        resizeTimeout = undefined;
        updateDimensions();
      }, 50); // ~20fps target update; actual visual is smoothed via RAF
    };
    
    window.addEventListener('resize', throttledUpdateDimensions);
    
    // Also observe container size changes (e.g., when sidebar toggles)
    const container = containerRef.current;
    let ro: ResizeObserver | null = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => throttledUpdateDimensions());
      ro.observe(container);
    }
    
    return () => {
      window.removeEventListener('resize', throttledUpdateDimensions);
      if (resizeTimeout !== undefined) window.clearTimeout(resizeTimeout);
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = null;
      if (ro && container) ro.unobserve(container);
    };
  }, [updateDimensions]);

  // Center origin only once on initial mount (do not recenter on subsequent width changes)
  const hasCenteredRef = useRef(false);
  useEffect(() => {
    if (!hasCenteredRef.current && isInitialized && dimensions.width > 0) {
      dispatch(centerOrigin({ width: dimensions.width, height: dimensions.height }));
      hasCenteredRef.current = true;
    }
  }, [isInitialized, dimensions.width, dimensions.height, dispatch]);

  // Sync preferred orientation from preferences to viewport
  // This ensures the viewport orientation updates when the user changes their preference
  useEffect(() => {
    if (preferences.preferredOrientation) {
      dispatch(setOrientation(preferences.preferredOrientation));
    }
  }, [preferences.preferredOrientation, dispatch]);

  // Update dimensions when orientation changes to optimize layout
  useEffect(() => {
    // Reset max dimensions to allow proper resizing for new orientation
    maxDimensionsRef.current = isVertical 
      ? { width: 600, height: 600 } 
      : { width: 800, height: 400 };
    updateDimensions();
  }, [isVertical, updateDimensions]);

  // Handle URL parameters for scene sharing
  useEffect(() => {
    const url = new URL(window.location.href);
    // Check for share parameter (primary), then fallback to legacy parameters
    const shareParam = url.searchParams.get('share');
    const sceneParam = url.searchParams.get('scene') || url.searchParams.get('s');
    const codeParam = url.searchParams.get('code') || url.searchParams.get('c');
    
    const detectedParam = shareParam || sceneParam || codeParam;
    
    if (detectedParam && !shareUrlState.detectedSceneId) {
      console.log('Detected share parameter:', detectedParam);
      dispatch(setDetectedSceneId(detectedParam));
    }
  }, [dispatch, shareUrlState.detectedSceneId]);

  // Handle auto-load prompt actions
  const handleAutoLoad = async () => {
    if (!shareUrlState.detectedSceneId) return;
    
    dispatch(setAutoLoadingState(true));
    dispatch(setAutoLoadError(null));
    
    try {
      console.log('Auto-load requested for scene:', shareUrlState.detectedSceneId);
      
      // Load the shared scene from the API
      const result = await loadSharedScene(shareUrlState.detectedSceneId);
      console.log('Scene loaded:', result.metadata);
      
      const state = result.state;
      
      // Load all state slices from the shared scene
      if (state.viewport) {
        dispatch(loadViewportState(state.viewport));
      }
      if (state.numberLines) {
        dispatch(loadNumberLineState(state.numberLines));
      }
      if (state.secondaryLine) {
        dispatch(loadSecondaryLineState(state.secondaryLine));
      }
      if (state.equivalentFractions) {
        dispatch(loadEquivalentFractionsState(state.equivalentFractions));
      }
      if (state.expressions) {
        dispatch(loadExpressionsState(state.expressions));
      }
      if (state.jumpIndicators) {
        dispatch(loadJumpIndicatorsState(state.jumpIndicators));
      }
      if (state.multipliers) {
        dispatch(setMultiplierMarkers(state.multipliers));
      }
      if (state.annotations) {
        dispatch(loadAnnotationsState(state.annotations));
      }
      if (state.constants) {
        dispatch(loadConstantsState(state.constants));
      }
      if (state.preferences) {
        dispatch(updatePreferences(state.preferences));
      }
      if (state.labelVisibility) {
        dispatch(loadLabelVisibilityState(state.labelVisibility));
      }
      if (state.customTicks) {
        dispatch(loadCustomTicksState(state.customTicks));
      }
      if (state.dnlTools) {
        dispatch(loadDNLToolsState(state.dnlTools));
      }
      if (state.measurements) {
        dispatch(loadMeasurementsState(state.measurements));
      }
      
      // Mark as loaded from share
      dispatch(setLoadedFromShare(true));
      dispatch(dismissAutoLoadPrompt());
      
      console.log('Scene loaded successfully from URL');
      
      // Clean up URL after successful load
      const url = new URL(window.location.href);
      url.searchParams.delete('scene');
      url.searchParams.delete('s');
      url.searchParams.delete('code');
      url.searchParams.delete('c');
      url.searchParams.delete('share');
      window.history.replaceState({}, '', url.toString());
      
    } catch (error) {
      console.error('Auto-load failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load shared scene';
      dispatch(setAutoLoadError(errorMessage));
    } finally {
      dispatch(setAutoLoadingState(false));
    }
  };

  const handleDismissAutoLoad = () => {
    dispatch(dismissAutoLoadPrompt());
  };

  return (
    <ModalProvider>
      <AppShell numberLineRef={numberLineRef}>
        {/* FIXED: Main Canvas Container with stable sizing */}
        <div 
          ref={containerRef}
          className={`flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 ${
            isFullscreen ? 'p-2' : 'p-4'
          }`}
          style={{ 
            minHeight: isFullscreen ? '100%' : '500px', // Full height in fullscreen
            maxHeight: '100vh', // Prevent growing beyond viewport
            overflow: 'hidden' // Prevent scrollbars affecting size calculation
          }}
        >
          {/* T-37: Use NumberLineCanvas with stable dimensions */}
          {/* Viewport box clips the inner canvas; inner keeps max size to lock scale */}
          <div
            className="relative"
            style={{ width: dimensions.width, height: dimensions.height, overflow: 'hidden' }}
          >
            <div
              className="absolute"
              style={{
                width: maxDimensionsRef.current.width,
                height: maxDimensionsRef.current.height,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <NumberLineCanvas
                width={maxDimensionsRef.current.width}
                height={maxDimensionsRef.current.height}
                viewWidth={dimensions.width}
                viewHeight={dimensions.height}
                isFullscreen={isFullscreen}
                className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm bg-white dark:bg-gray-800"
                exportRef={numberLineRef}
              />
            </div>
          </div>
          

        </div>

        {/* Auto-load prompt overlay */}
        {shareUrlState.showAutoLoadPrompt && shareUrlState.detectedSceneId && (
          <AutoLoadPrompt
            sceneId={shareUrlState.detectedSceneId}
            isLoading={shareUrlState.isAutoLoading}
            error={shareUrlState.autoLoadError}
            onAutoLoad={handleAutoLoad}
            onOpenLoadDialog={handleDismissAutoLoad}
            onDismiss={handleDismissAutoLoad}
          />
        )}

        {/* PWA Install Prompt (unchanged) */}
        <InstallPrompt />

        {/* Offline Status Indicator (unchanged) */}
        <OfflineStatus />
      </AppShell>
    </ModalProvider>
  );
}

export default App;